import React, { useEffect, useState } from "react";
import { FaWhatsapp, FaTelegramPlane, FaEnvelope, FaShareAlt } from "react-icons/fa";
import { LuX, LuLink, LuCopy, LuCheck } from "react-icons/lu";
import style from "./Monetization.module.css";
import { creatorInviteUrl } from "../../utils/creatorInviteUrl";

// Share an invited creator's signup link (the same one the invite email
// carries) when the email doesn't arrive: WhatsApp / Telegram / email / copy,
// plus the device's own share sheet where the browser supports it.
export default function ShareInviteModal({ creator, onClose }) {
  const url = creatorInviteUrl(creator.email);
  const message = `Hi ${creator.name}, you've been invited to join as a creator. Complete your signup here: ${url}`;
  const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy the invite link:", url);
    }
  };

  const nativeShare = async () => {
    try {
      await navigator.share({ title: "Creator invitation", text: message, url });
    } catch (err) {
      if (err?.name !== "AbortError") copy();
    }
  };

  const open = (href) => window.open(href, "_blank", "noopener,noreferrer");
  const enc = encodeURIComponent;
  const options = [
    { key: "whatsapp", label: "WhatsApp", Icon: FaWhatsapp, color: "#25D366", onClick: () => open(`https://wa.me/?text=${enc(message)}`) },
    { key: "telegram", label: "Telegram", Icon: FaTelegramPlane, color: "#229ED9", onClick: () => open(`https://t.me/share/url?url=${enc(url)}&text=${enc(message.replace(` ${url}`, ""))}`) },
    { key: "email", label: "Email", Icon: FaEnvelope, color: "#EA4335", onClick: () => { window.location.href = `mailto:${creator.email}?subject=${enc("Your creator invitation")}&body=${enc(message)}`; } },
    ...(canNativeShare
      ? [{ key: "more", label: "More", Icon: FaShareAlt, color: "#c98a4b", onClick: nativeShare }]
      : []),
  ];

  return (
    <div className={style.modalOverlay} onClick={onClose}>
      <div
        className={style.shareModal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-invite-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={style.shareHeader}>
          <div>
            <h3 id="share-invite-title" className={style.shareTitle}>Share invite link</h3>
            <p className={style.shareSub}>
              For <strong>{creator.name}</strong> · {creator.email}
            </p>
          </div>
          <button type="button" className={style.shareClose} onClick={onClose} aria-label="Close">
            <LuX size={18} />
          </button>
        </div>

        <div className={style.shareLinkRow}>
          <LuLink size={16} className={style.shareLinkIcon} />
          <input
            className={style.shareLinkInput}
            value={url}
            readOnly
            onFocus={(e) => e.target.select()}
            aria-label="Invite link"
          />
          <button
            type="button"
            className={`${style.shareCopyBtn} ${copied ? style.shareCopyBtnDone : ""}`}
            onClick={copy}
          >
            {copied ? <LuCheck size={15} /> : <LuCopy size={15} />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>

        <div className={style.shareDivider}>
          <span>or share via</span>
        </div>

        <div className={style.shareRow}>
          {options.map(({ key, label, Icon, color, onClick }) => (
            <button key={key} type="button" className={style.shareOption} onClick={onClick}>
              <span
                className={style.shareOptionIcon}
                style={{ color, background: `${color}1f`, borderColor: `${color}40` }}
              >
                <Icon size={22} />
              </span>
              <span className={style.shareOptionLabel}>{label}</span>
            </button>
          ))}
        </div>

        <p className={style.shareNote}>
          Use this if the invitation email didn't arrive. The link opens the creator signup page
          with their email filled in.
        </p>
      </div>
    </div>
  );
}
